import { useMemo } from "react";
import { getAllSceneNodes } from "@/document/model/document";
import { documentStore, selectionStore, uiStore } from "@/editor/store";
import { useStoreSubscribe } from "@/lib/store";

interface StatusBarProps {
  zoom: number;
  tool: string;
}

export function StatusBar({ zoom, tool }: StatusBarProps) {
  const document = useStoreSubscribe(documentStore.document);
  const selection = useStoreSubscribe(selectionStore.selection);
  const pointerStatus = useStoreSubscribe(uiStore.pointerStatus);
  const message = useStoreSubscribe(uiStore.message);

  const selectedCount = selection.selectedNodeIds.length;
  const nodeCount = useMemo(() => getAllSceneNodes(document).length, [document, document.revision]);
  const selectionSummary = selectedCount > 0 ? `${selectedCount} selected` : `${nodeCount} nodes`;

  return (
    <footer className="flex h-7 shrink-0 items-center justify-between border-t border-white/10 bg-[#1f2329] px-3 text-[11px] text-slate-400">
      <div className="flex items-center gap-4">
        <span>{Math.round(zoom * 100)}%</span>
        <span>{tool}</span>
        <span>{selectionSummary}</span>
        <StatusCoordinate label="Canvas" point={pointerStatus?.canvas} />
        <StatusCoordinate label="Frame" point={pointerStatus?.frame?.position} />
      </div>
      <span>{message}</span>
    </footer>
  );
}

function StatusCoordinate({ label, point }: { label: string; point?: { x: number; y: number } }) {
  return (
    <span className="tabular-nums text-slate-500">
      {label} {point ? `${formatCoordinate(point.x)}, ${formatCoordinate(point.y)}` : "--, --"}
    </span>
  );
}

function formatCoordinate(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}
